
import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';

import { Button } from '@mui/material';

import Grid from '@mui/material/Grid2';

import { ModelBase, PlayerObject, SmartHash } from "@models";
import { Creature } from "@dndModels";
import { DATA_TYPE_INFO } from "@dndModels/Constants";

import API from "@dndUtilities/system_api";
import CommonUtilities from "@utilities/common_utilities";

import NonFSIndex from "@dndComponents/NonFSIndex";
import ToastBox from "@components/display/ToastBox";
import CopyButton from "@components/input/buttons/CopyButton";
import AddButton from "@components/input/buttons/AddButton";
import ModalOrDrawer from "@components/display/ModalOrDrawer";
import ButtonBox from "@components/input/buttons/ButtonBox";
import LoadingBox from "@components/input/LoadingBox";
import CreatureManager from "@dndViews/creature/CreatureManager";

const api = API.getInstance();
const common_util = CommonUtilities.getInstance();

export default function CreatureIndex() {
  const [fetched, setFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loading_message, setLoadingMessage] = useState("Loading");
  const [percentage, setPercentage] = useState(0);
  const [toast_time, setToastTime] = useState(-1);
  const [navigateTo, setNavigateTo] = useState<string | null>(null);
  const [copying_part_of, setCopyingPartOf] = useState<Creature | null>(null);
  const [filtered, setFiltered] = useState<string[]>([]);
  const [open, setOpen] = useState(false);

  const renderExtras = () => {
    return (
      <>
        <ModalOrDrawer
          open={open}
          width={800}
          onClose={() => { setOpen(false); }}>
          <CreatureManager />
        </ModalOrDrawer>
        <ModalOrDrawer
          open={copying_part_of !== null}
          onClose={() => { setCopyingPartOf(null); }}
        >
          { copying_part_of ?
            <Grid container spacing={0} direction="row">
              <Grid
                className="header centered"
                size={{
                  xs: 12
                }}>Choose What to Copy</Grid>
              <Grid
                size={{
                  xs: 3
                }}>
                <ButtonBox 
                  name="Main"
                  onClick={() => {
                    api.getFullObject("creature", copying_part_of._id).then(res => {
                      if (res instanceof Creature) {
                        navigator.clipboard.writeText(JSON.stringify(res.table));
                        setToastTime(common_util.get_time_number());
                        setCopyingPartOf(null);
                      }
                    });
                  }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 3
                }}>
                <ButtonBox 
                  name="Spellcasting"
                  onClick={() => {
                    api.getFullObject("creature", copying_part_of._id).then(res => {
                      if (res instanceof Creature) {
                        navigator.clipboard.writeText(JSON.stringify(res.spellcasting_table));
                        setToastTime(common_util.get_time_number());
                        setCopyingPartOf(null);
                      }
                    });
                  }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 3
                }}>
                <ButtonBox 
                  name="Attributes"
                  onClick={() => {
                    api.getFullObject("creature", copying_part_of._id).then(res => {
                      if (res instanceof Creature) {
                        navigator.clipboard.writeText(JSON.stringify(res.attributes_table));
                        setToastTime(common_util.get_time_number());
                        setCopyingPartOf(null);
                      }
                    });
                  }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 3
                }}>
                <ButtonBox 
                  name="Ability Scores"
                  onClick={() => {
                    api.getFullObject("creature", copying_part_of._id).then(res => {
                      if (res instanceof Creature) {
                        navigator.clipboard.writeText(JSON.stringify(res.ability_scores));
                        setToastTime(common_util.get_time_number());
                        setCopyingPartOf(null);
                      }
                    });
                  }}
                />
              </Grid>
            </Grid>
          : 
            <></> 
          }
        </ModalOrDrawer>
        <ToastBox
          openTime={ toast_time }
        >
          Copied to Clipboard
        </ToastBox>
      </>
    );
  }

  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      setLoading(true);
      const onComplete = (res: SmartHash) => {
        if (res.creature) {
          setLoading(false);
        }
      }
  
      const onProgress = (data_type: string, percent: number ) => {
        const dt_info = DATA_TYPE_INFO.find(o => o.data_type === data_type);
        if (dt_info) {
          setPercentage(percent * 100);
          setLoadingMessage(`Loading ${dt_info.plural}`);
        }
      };
      // Loading these is to make it so the details show properly
      api.getSetOfObjectsWithCallbacks({ 
        data_types: ["condition","creature","spell"],
        onComplete, onProgress 
      });
    }
  }, [fetched, setFetched]);

  const renderClipboardAll = () => {
    return (
      <CopyButton
        label="Clipboard All"
        disabled={filtered.length === 0}
        onClick={() => {
          api.getFullObjectsForClipboard("creature", filtered).then(res => {
            navigator.clipboard.writeText(JSON.stringify(res));
            setToastTime(common_util.get_time_number());
          });
        }}
      />
    );
  }
  
  if (!fetched || loading) {
    const height = window.innerHeight - 81;
    return (<LoadingBox message={loading_message} percentage={percentage} size={height * 0.5} height={height} width={window.innerWidth - 1} />);
  } else if (navigateTo !== null) {
    return <Navigate to={navigateTo} />;
  } else {
    return (
      <>
        <NonFSIndex
          data_type="creature"
          show_image
          // save_all
          // log_it
          // sort={(obj1: ModelBase | PlayerObject, obj2: ModelBase | PlayerObject) => {
          //   if (obj1 instanceof Creature && obj2 instanceof Creature) {
          //     return obj1.challenge_rating > obj2.challenge_rating ? 1 : (obj1.challenge_rating === obj2.challenge_rating ? 0 : -1);
          //   }
          //   return 0;
          // }}
          // extra_filter={(obj: ModelBase | PlayerObject) => {
          //   if (obj instanceof Creature && api.temp_hash.creature && api.temp_hash.creature.objects) {
          //     const others_with_name = api.temp_hash.creature.objects.filter(o => o.name === obj.name && o._id !== obj._id && o.variant_group_id !== obj.variant_group_id);
          //     return others_with_name.length > 0;
          //   }
          //   return false;
          // }}
          extra_by_add={api.user && api.user.admin ? () => {
            // debugger;
            return (
              <>
                &nbsp;
                <AddButton
                  label="Merge Add"
                  onClick={() => {
                    setNavigateTo(`/dandd/creature/merge_add`);
                  }}
                />
                &nbsp;
                <AddButton
                  label="Parse Add"
                  onClick={() => {
                    setNavigateTo(`/dandd/creature/parse_add`);
                  }}
                />
                &nbsp;
                <Button
                  variant="contained" color="primary" 
                  onClick={() => {
                    setOpen(true);
                  }}>
                  Creature Manager
                </Button>
                { renderClipboardAll() }
              </>
            );
          } : () => { return renderClipboardAll(); }}
          get_filtered={setFiltered}
          extra={(copyMe: ModelBase | PlayerObject) => {
            if (copyMe instanceof Creature && api.temp_hash.creature && api.temp_hash.creature.objects) {
              // const others_with_name = api.temp_hash.creature.objects.filter(o => o.name === copyMe.name && o._id !== copyMe._id && o.variant_group_id !== copyMe.variant_group_id);

              return (
                <Grid container spacing={0} direction="row">
                  {/* ({ copyMe.source_book_abbrev }){ others_with_name.length > 0 ? `${others_with_name.length} Others` : "" } */}
                  <Grid
                    size={{
                      xs: 6
                    }}>
                    <CopyButton
                      label="Clipboard"
                      onClick={() => {
                        api.getFullObjectForClipboard("creature", copyMe._id).then(res => {
                          navigator.clipboard.writeText(JSON.stringify(res));
                          setToastTime(common_util.get_time_number());
                        });
                      }}
                    />
                  </Grid>
                  <Grid
                    size={{
                      xs: 6
                    }}>
                    <CopyButton
                      label="Part"
                      onClick={() => {
                        setCopyingPartOf(copyMe);
                      }}
                    />
                  </Grid>
                </Grid>
              );
            }
          }}
          quick_fields={["creature_type","subtypes","creature_group","min_cr","max_cr"]}
        />
        { renderExtras() }
      </>
    );
  }
}
